"use client";

import { ArrowRight, Minus, Plus } from "lucide-react";
import { useState } from "react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  numeral: "I",
  question: "How is your seafood sourced and harvested?",
  intro: "Every catch is wild-harvested by our crew aboard the Miss Yvonne, following strict sustainable practices that protect the Gulf Coast ecosystem for future generations.",
  bullets: ["Wild-caught using responsible, selective fishing methods", "Directly sourced from the Gulf of Mexico waters", "Traceable from the vessel deck to your doorstep"]
}, {
  numeral: "II",
  question: "How do you ensure freshness during delivery?",
  intro: "We utilize professional-grade cold chain logistics to ensure your seafood arrives in peak condition, maintaining the integrity of the catch from our freezer to your kitchen.",
  bullets: ["Flash-frozen at sea to lock in ocean-fresh quality", "Insulated, eco-friendly packaging for temperature control", "Overnight shipping to preserve texture and flavor"]
}, {
  numeral: "III",
  question: "What is included in the sampler boxes?",
  intro: "Our curated boxes are designed to showcase the variety of our catch, featuring a selection of our finest fillets and ready-to-eat products, perfect for any home chef.",
  bullets: ["Deckhand Sampler: A variety of our most popular seasonal fillets", "Captain Sampler: A premium selection of our signature wild-caught cuts", "Detailed preparation guides included in every box"]
}, {
  numeral: "IV",
  question: "Do you offer wholesale or commercial accounts?",
  intro: "We proudly partner with restaurants, markets, and culinary institutions that value heritage-quality, wild-caught seafood. Contact our team to discuss your specific needs.",
  bullets: ["Custom volume pricing for commercial partners", "Consistent supply chain for seasonal menu planning", "Direct communication with our vessel management team"]
}, {
  numeral: "V",
  question: "How should I store my seafood upon arrival?",
  intro: "To maintain the premium quality of your Miss Yvonne catch, we recommend keeping your items frozen until you are ready to prepare them, then thawing slowly in the refrigerator.",
  bullets: ["Keep frozen at 0°F or below until use", "Thaw in the refrigerator for 12-24 hours for best results", "Consume within 48 hours of thawing for optimal freshness"]
}, {
  numeral: "VI",
  question: "Can I track my order after it ships?",
  intro: "Once your order leaves our facility, you will receive a tracking number via email, allowing you to monitor your delivery as it makes its way from our shore to your door.",
  bullets: ["Real-time tracking updates provided via email", "Dedicated support for any delivery inquiries", "Carefully scheduled shipping to avoid weekend delays"]
}, {
  numeral: "VII",
  question: "What is your return policy?",
  intro: "We stand behind every catch. If you are not completely satisfied with the quality of your order, please contact us within 24 hours of delivery so we can make it right.",
  bullets: ["Quality guarantee on all wild-caught products", "Prompt resolution for any shipping or handling concerns", "Dedicated customer care for all Miss Yvonne patrons"]
}];
export default function Support() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: accordionRef,
    isVisible: accordionVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  const [openItem, setOpenItem] = useState<string>("");
  return <section data-section-id="4182" id="customer-support-info" className="bg-background text-foreground py-24 md:py-32 lg:py-[120px] px-6 md:px-10 lg:px-16">
      <div className="mx-auto max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5">
            <div ref={headerRef} className={`lg:sticky lg:top-24 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <p className="font-mono text-xs tracking-[0.25em] uppercase mb-8 text-primary">
                — Frequently Asked
              </p>

              <h2 className="font-default text-4xl md:text-5xl leading-[1.05] tracking-tight text-primary mb-8">
                From Shore
                <br />
                <em className="font-default italic font-light">
                  To Your Door.
                </em>
              </h2>

              <p className="font-default text-base leading-relaxed text-foreground max-w-md mb-10">
                Answers to the questions most frequently raised by our patrons. Should you require further assistance, our crew is always ready to help.
              </p>

              <Link to="/ContactPage" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase border-b border-transparent pb-1 transition-colors duration-300 hover:opacity-80 text-primary border-primary">
                Still curious? Contact us
                <ArrowRight className="size-3.5" strokeWidth={1.5} />
              </Link>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <div ref={accordionRef} className={`transition-all duration-1000 ease-out ${accordionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <Accordion type="single" collapsible value={openItem} onValueChange={setOpenItem} className="w-full">
                {faqs.map((faq, i) => {
                const itemValue = `item-${i}`;
                const isOpen = openItem === itemValue;
                return <AccordionItem key={itemValue} value={itemValue} data-index={i} className="border-b border-border first:border-t first:border-border">
                      <AccordionTrigger className="group flex items-start gap-6 py-7 hover:no-underline [&>svg]:hidden text-left">
                        <span className="font-mono text-xs tracking-[0.2em] uppercase pt-1.5 w-8 shrink-0 text-primary">
                          {faq.numeral}
                        </span>

                        <span className="flex-1 font-default text-xl md:text-[20px] leading-snug text-foreground font-normal">
                          {faq.question}
                        </span>

                        <span className="shrink-0 pt-1.5 transition-transform duration-300 text-primary">
                          {isOpen ? <Minus className="size-5" strokeWidth={1.25} /> : <Plus className="size-5" strokeWidth={1.25} />}
                        </span>
                      </AccordionTrigger>

                      <AccordionContent className="pb-8 pt-0">
                        <div className="pl-14 pr-12 md:pr-16">
                          <p className="font-default text-base text-foreground mb-5" style={{
                        lineHeight: 1.7
                      }}>
                            {faq.intro}
                          </p>
                          <ul className="space-y-2.5">
                            {faq.bullets.map((bullet, j) => <li key={j} data-index={j} className="flex gap-3 font-default text-base text-foreground" style={{
                          lineHeight: 1.7
                        }}>
                                <span className="font-default shrink-0 select-none text-primary">
                                  —
                                </span>
                                <span>{bullet}</span>
                              </li>)}
                          </ul>
                        </div>
                      </AccordionContent>
                    </AccordionItem>;
              })}
              </Accordion>
            </div>
          </div>
        </div>
      </div>
    </section>;
}